import { AgeVerificationMethodPriority, AgeVerificationStatus } from "@next-platform/packages-ts-types";
import { Button, ButtonSize, ButtonVariant, Icon, IconSize, IconVariants, Spinner, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { IAuthenticatedUser, NotificationType, useAgeVerification, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AgeVerificationMethods, PageLayout, PageSize } from "@/components";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import useEnsureUserProfile from "@/features/user/utils/useEnsureUserProfile";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";

import { getProviderInfo } from "./utils/getProviderInfo";

/**
 * Age Verification Callback Page Logic:
 * - When the page loads, we first attempt to retrieve the "provider info". This
 *   info comes from the URL query string.
 * - If any of this information is missing, we do not proceed with the Finalize API call,
 *   and we simply display the default message: "Couldn't confirm your age."
 * - If all required info is available, we call the Finalize API to complete the flow.
 * - After calling the Finalize API:
 *    - If the API returns a payload containing `"verified": true`, we display the success message.
 *    - For any other result (error or not verified), we revert to the default message.
 * - If the Finalize API does not confirm verification, as an extra safeguard,
 *   we also check the user's profile (`userProfile.age_verification.status`) to see if
 *   their status is already `VERIFIED`. If so, we treat the user as verified and show a confirmation.
 * - Additionally, if neither the Finalize API nor the user's profile confirm verification,
 *   we provide the user with the option to use a fallback age verification method by displaying
 *   the available fallback methods (via the AgeVerificationMethods component).
 *
 * This process ensures that only users who successfully complete verification,
 * or who are already verified in their profile, will see a confirmation, while
 * all other scenarios fall back to the default experience with the option to retry or use an alternative method.
 */
const AgeVerificationCallback = () => {
  const navigate = useNavigate();
  const { onUserPostAgeVerificationFinalize } = useAgeVerification();
  const providerInfo = getProviderInfo();
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState<boolean>();
  const { userProfile } = useUserInfo();
  const { displayToast } = useToast();
  const { setAuthenticatedUser } = useEnsureUserProfile();
  const { user } = useAuth();
  const { t } = useTranslation();

  const handleContinue = () => navigate(ROUTES.home);

  useEffect(() => {
    const finalize = async () => {
      if (!providerInfo) {
        setIsVerified(false);
        setIsLoading(false);
        displayToast({ message: t("ageVerification:section.failure.title"), type: NotificationType.error });
        return;
      }

      const result = await onUserPostAgeVerificationFinalize({
        callbackParams: {
          ...providerInfo,
        },
      });

      if (result?.data?.verified) {
        setIsVerified(true);
        setIsLoading(false);
        displayToast({ message: t("ageVerification:section.success.title"), type: NotificationType.success });
        return setAuthenticatedUser({ ...user } as IAuthenticatedUser);
      }
      if (result?.error) {
        displayToast({ message: result.error, type: NotificationType.default });
      } else {
        displayToast({ message: t("ageVerification:section.failure.title"), type: NotificationType.error });
      }
      setIsVerified(false);
      setIsLoading(false);
      return;
    };

    finalize();
  }, []);

  useEffect(() => {
    // it is possible that when the page load the user is already verified
    // we need do double check in his user profile if he is already verified
    // this could happen if the user refresh the page for example
    if (!isVerified && !isLoading && userProfile?.age_verification?.status === AgeVerificationStatus.VERIFIED) {
      setIsVerified(true);
    }
  }, [userProfile, isVerified, isLoading]);

  if (isLoading) {
    // show a spinner if the finalize api is still loading
    return <Spinner />;
  }

  if (isVerified) {
    // show a success message if the verification is successful
    return (
      <PageLayout size={PageSize.Small} className="flex min-h-screen items-center justify-center flex-col gap-xl">
        <div className="flex items-center gap-md flex-col -mt-[100px]">
          <Icon icon={IconVariants.CHECK_CIRCLE} size={IconSize.XLARGE} className="text-green-10" />
          <Text variant={TextVariants.HEADING_6_BOLD}>
            <Trans i18nKey="ageVerification:section.verified.title" />
          </Text>
          <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="text-center">
            <Trans i18nKey="ageVerification:section.success.description" />
          </Text>
        </div>

        <Button variant={ButtonVariant.CONTAINED} size={ButtonSize.LARGE} onClick={handleContinue}>
          <Trans i18nKey="ageVerification:section.success.cta.continue" />
        </Button>
      </PageLayout>
    );
  }

  // show a message if the verification is not found
  // maybe it's because the user age_verification is not verified or provider info are missing in local storage
  return (
    <PageLayout size={PageSize.Small} className="flex min-h-screen items-center justify-center flex-col gap-xl">
      <div className="flex items-center gap-md flex-col -mt-[100px]">
        <Icon icon={IconVariants.CLOSE_CIRCLE} size={IconSize.XLARGE} className="text-red-9" />
        <Text variant={TextVariants.HEADING_5_BOLD}>
          <Trans i18nKey="ageVerification:section.failure.title" />
        </Text>
        <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="text-center">
          <Trans i18nKey="ageVerification:section.failure.description" />
        </Text>
      </div>
      <Text variant={TextVariants.LABEL_LARGE_BOLD} className="text-center">
        <Trans i18nKey="ageVerification:section.failure.section.title" />
      </Text>
      <AgeVerificationMethods priority={AgeVerificationMethodPriority.FALLBACK} />
      <a href="https://docs.spicychat.ai/advanced/age-verification" target="_blank" className="text-blue-10 hover:underline">
        <Trans i18nKey="ageVerification:section.help.link.faqDocs" />
      </a>
    </PageLayout>
  );
};

export default AgeVerificationCallback;
